import { CoreScaleOptions, LinearScaleOptions, Scale } from "chart.js/auto";
import { DataType } from "@/types/DataType";
import { Logger } from "@/types/Logger";
import { useByteUtils } from "./useByteUtils";

const { float_to_bytes, int32_to_bytes, uint16_to_bytes, uint32_to_bytes } =
  useByteUtils();

export function useUtils($log?: Logger) {
  function isNullOrUndefined(value: unknown): value is null | undefined {
    return value === undefined || value === null;
  }

  function valueToInt(value: string | number) {
    return parseInt(String(value));
  }

  function valueToFloat(value: string | number) {
    return parseFloat(String(value));
  }

  function valueToWriteArray(
    value: unknown,
    paramType: DataType | "0" | null,
    mbusAddrLength: number,
  ): number[] {
    // We cannot be 100% sure that this function will be called with a `value`
    // parameter that is indeed of the type that `paramType` describes.
    // Check, check, doublecheck.
    const isValueNumberOrString =
      typeof value === "string" || typeof value === "number";

    if (paramType === "bool") {
      return value ? [0, 1] : [0, 0];
    } else if (paramType === "uint16_t" && isValueNumberOrString) {
      return Array.from(uint16_to_bytes(valueToInt(value)));
    } else if (paramType === "uint32_t" && isValueNumberOrString) {
      return Array.from(uint32_to_bytes(valueToInt(value)));
    } else if (paramType === "int" && isValueNumberOrString) {
      return Array.from(int32_to_bytes(valueToInt(value)));
    } else if (paramType === "float" && isValueNumberOrString) {
      return Array.from(float_to_bytes(valueToFloat(value)));
    } else if (paramType === "char2") {
      const writeBytes = new Uint8Array(mbusAddrLength * 2);
      const writeValue = typeof value === "string" ? value : "";
      for (let i = 0; i < writeValue.length; i++) {
        writeBytes[i] = writeValue.charCodeAt(i);
      }
      return Array.from(writeBytes);
    }
    $log?.error(
      `Type incompatibility: modbusAddrParams.type asserts "${paramType}",`,
      `but value is of type "${typeof value}".`,
    );
    return [];
  }

  function isCanvas(
    element?: HTMLElement | null,
  ): element is HTMLCanvasElement {
    return !!element && element.tagName === "CANVAS";
  }

  function isScaleLinearScaleOptions(
    scale: Scale<CoreScaleOptions>,
  ): scale is Scale<LinearScaleOptions> {
    return scale.type === "linear";
  }

  function clamp(x: number, min: number, max: number) {
    return Math.min(Math.max(x, min), max);
  }

  return {
    valueToWriteArray,
    isCanvas,
    isScaleLinearScaleOptions,
    isNullOrUndefined,
    clamp,
  };
}
